/**
 * All Walks — every walk in the workspace, newest first.
 *
 * The console could open ONE walk and could see a project's most recent few;
 * there was no page that answered "what has been walked". This is it, and it
 * is where the Brief's *walks captured* tile now lands — a number whose only
 * previous destination was an in-page scroll to a coverage block that answers
 * a different question ("which projects are cold"), not "which walks are
 * those".
 *
 * ## Every row goes somewhere
 *
 * Two destinations per row, because a walk belongs to two things: the walk
 * itself (the row) and its project (the dot and the project name). Both are
 * real anchors carrying the current `?workspace=`, per the same rule the rest
 * of the console follows.
 *
 * ## What a row states, and what it refuses to
 *
 * Date, project, who walked it, recorded length, and how many records it
 * produced. `recordedMinutes` is absent — not zero — for a walk still
 * recording, and the row says "in progress" rather than printing `0 MIN`,
 * which would assert something false about a walk that is happening right
 * now. `itemCount: 0` IS printed, because a walk that produced nothing is a
 * real and interesting answer.
 *
 * There is no audio, no player and no transcript here (spec §7.3). Length is
 * visit metadata rather than audio content — the ruling `walkDuration.ts`
 * records — and it is the only measurement on the row.
 *
 * ## The cap states itself
 *
 * `total` is the real number in scope, so a capped list prints "showing 200
 * of 431" instead of quietly looking complete.
 */

import { useMemo, useState } from "react";
import {
  fetchWalks,
  type ApiError,
  type ConsoleSite,
  type ConsoleWalkResponse,
  type WalksResponse,
} from "../api";
import { useResource } from "../useResource";
import { resourceQuery } from "../resourceCache";
import { LIST_TYPE } from "../listTypography";
import { walkerLabel } from "./walkerLabel";
import { MenuControl } from "../components/MenuControl";
import {
  inWalkRange,
  walkerOptions,
  WALK_ORDER_LABEL,
  WALK_RANGE_LABEL,
  type WalkOrder,
  type WalkRange,
} from "../lib/walkFilters";
import { C, FONT_MONO, projectDot } from "../tokens";
import {
  Card,
  ASK_COMPOSER_CLEARANCE,
  ConsoleViewProps,
  EmptyPanel,
  Eyebrow,
  FailurePanel,
  FilterChip,
  META,
  StaleBanner,
  Pill,
  PagedListEnd,
  TABLE_COLUMN_WIDTH,
  ViewBody,
  ViewHeader,
  clockOf,
  dayLabel,
  plural,
  usePagedList,
  scopedSearch,
} from "../components/chrome";
import { SkeletonBlock } from "../components/Skeleton";

/** What a row prints for a length that is not yet knowable. See the header. */
function lengthLabel(walk: ConsoleWalkResponse): string {
  if (walk.recordedMinutes === undefined) {
    return walk.endedAt ? "no recording" : "in progress";
  }
  return `${plural(walk.recordedMinutes, "min", "min")} recorded`;
}

/**
 * The one status pill on a row's right (design 1a): it carries BOTH "has
 * records" and "reviewed" — gold while any record is still pending, green
 * once everything produced is through review, quiet grey when the walk
 * produced nothing (a real answer, not a gap — see the file header).
 */
type ReviewState = "needs_review" | "saved_for_later" | "reviewed" | "no_records";

/** Records set aside for later are still to review (the phone's Review Later
 *  Items): a walk holding only those is not Reviewed. It reads "N later" and
 *  sits under Needs review, where a reviewer looks for it. */
function reviewStateOf(walk: ConsoleWalkResponse): ReviewState {
  if (walk.pendingReviewCount > 0) return "needs_review";
  if ((walk.savedForLaterCount ?? 0) > 0) return "saved_for_later";
  return walk.itemCount > 0 ? "reviewed" : "no_records";
}

const needsReviewing = (walk: ConsoleWalkResponse) => {
  const state = reviewStateOf(walk);
  return state === "needs_review" || state === "saved_for_later";
};

const REVIEW_PILL: Record<ReviewState, { label: string; ink: string; bg: string }> = {
  needs_review: { label: "Needs review", ink: C.amber, bg: C.sunk },
  saved_for_later: { label: "Later", ink: C.amber, bg: C.sunk },
  reviewed: { label: "Reviewed", ink: C.green, bg: C.greenSoft },
  no_records: { label: "No records", ink: C.sub2, bg: C.faint },
};

/** The Walks loader holds the same header, stat, filter, day and row geometry
 * as the loaded list, so loading never rearranges the page. */
export function WalksSkeleton() {
  return (
    <div
      className="sb"
      data-testid="loading-state"
      data-skeleton="walks"
      role="status"
      aria-label="Loading walks"
      aria-busy="true"
      style={{ flex: 1, minHeight: 0, overflow: "auto", padding: `22px 0 ${ASK_COMPOSER_CLEARANCE}px`, paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)` }}
    >
      <div className="pagecol" style={{ width: "100%", maxWidth: TABLE_COLUMN_WIDTH, margin: "0 auto" }}>
        <SkeletonBlock height={9} width={150} radius={5} />
        <SkeletonBlock height={28} width={132} radius={6} style={{ marginTop: 8 }} />
        <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
          {[104, 142, 112].map((width) => <SkeletonBlock key={width} height={30} width={width} radius={999} />)}
        </div>
        <SkeletonBlock height={10} width={164} radius={5} style={{ margin: "26px 0 8px 4px" }} />
        <div style={{ background: C.surface, border: `1px solid ${C.hair}`, borderRadius: 14, padding: "0 18px" }}>
          {[0, 1, 2, 3].map((index) => (
            <div key={index} style={{ height: 76, display: "flex", alignItems: "center", gap: 16, borderBottom: index === 3 ? "none" : `1px solid ${C.hair}` }}>
              <SkeletonBlock height={38} width={38} radius={10} />
              <div style={{ flex: 1 }}>
                <SkeletonBlock height={15} width={index % 2 ? "42%" : "57%"} radius={5} />
                <SkeletonBlock height={10} width="31%" radius={4} style={{ marginTop: 9 }} />
              </div>
              <SkeletonBlock height={24} width={92} radius={999} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** What a row prints alongside the walker: length, then photos. */
function factsLine(walk: ConsoleWalkResponse): string {
  const photos =
    walk.photoCount > 0 ? plural(walk.photoCount, "photo", "photos") : "no photos";
  return `${walkerLabel(walk)} · ${lengthLabel(walk)} · ${photos}`;
}

function WalkRow({
  walk,
  siteName,
  href,
  onOpen,
  projectHref,
  onOpenProject,
  first,
}: {
  walk: ConsoleWalkResponse;
  siteName: string | undefined;
  href: string;
  onOpen: () => void;
  projectHref: string;
  onOpenProject: () => void;
  first: boolean;
}) {
  const state = reviewStateOf(walk);
  const pill = state === "saved_for_later"
    ? { ...REVIEW_PILL.saved_for_later, label: `${walk.savedForLaterCount} later` }
    : REVIEW_PILL[state];
  return (
    <div
      data-testid="walk-row"
      data-walk-id={walk.id}
      className="wrowh"
      style={{
        display: "grid",
        gridTemplateColumns: "56px minmax(0, 1fr) 176px 108px 14px",
        alignItems: "center",
        gap: 16,
        padding: "14px 18px",
        borderTop: first ? "1px solid transparent" : `1px solid ${C.line}`,
      }}
    >
      {/* Time in the gutter — the day header owns the date. */}
      <span style={{ fontFamily: FONT_MONO, fontSize: LIST_TYPE.mono, fontWeight: 600, color: C.sub }}>
        {clockOf(new Date(walk.startedAt))}
      </span>

      <a
        href={href}
        data-testid="walk-row-open"
        onClick={(event) => {
          event.preventDefault();
          onOpen();
        }}
        style={{ minWidth: 0 }}
      >
        <span
          data-testid="row-title"
          style={{
            display: "block",
            fontSize: LIST_TYPE.title,
            fontWeight: LIST_TYPE.titleWeight,
            letterSpacing: "-0.3px",
            lineHeight: 1.3,
          }}
        >
          {walk.name || "Untitled walk"}
        </span>
        <span
          style={{
            display: "block",
            marginTop: 4,
            fontSize: LIST_TYPE.sub,
            fontWeight: 500,
            color: C.sub2,
          }}
        >
          {factsLine(walk)}
        </span>
      </a>

      {/* The project, as its own link — a walk belongs to a job site, and a
          reader scanning this list is as likely to want the project as the
          walk. */}
      <a
        href={projectHref}
        data-testid="walk-row-project"
        data-project-id={walk.jobSiteId}
        onClick={(event) => {
          event.preventDefault();
          onOpenProject();
        }}
        style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 4,
            background: projectDot(walk.jobSiteId),
            flexShrink: 0,
          }}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: LIST_TYPE.name,
            fontWeight: 600,
            color: C.sub,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {siteName || walk.jobSiteId}
        </span>
      </a>

      <span
        data-testid="walk-row-status"
        data-state={reviewStateOf(walk)}
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        <Pill label={pill.label} ink={pill.ink} bg={pill.bg} />
      </span>

      <span aria-hidden style={{ color: C.line2, fontSize: 14.95 }}>›</span>
    </div>
  );
}

/** Day buckets, in the list's own (newest-first) order. The day header owns
 *  the date so the rows don't repeat it (design 1a). */
function groupByDay(
  walks: ConsoleWalkResponse[],
): Array<{ label: string; walks: ConsoleWalkResponse[] }> {
  const days: Array<{ label: string; walks: ConsoleWalkResponse[] }> = [];
  for (const walk of walks) {
    const label = dayLabel(walk.startedAt);
    const last = days[days.length - 1];
    if (last && last.label === label) last.walks.push(walk);
    else days.push({ label, walks: [walk] });
  }
  return days;
}

/** A day's own meta: how many walks, and the minutes that are KNOWN — an
 *  in-progress walk adds nothing rather than a fake zero. */
function dayMeta(walks: ConsoleWalkResponse[]): string {
  const minutes = walks.reduce((sum, walk) => sum + (walk.recordedMinutes ?? 0), 0);
  return `${plural(walks.length, "walk", "walks")} · ${minutes} min`.toUpperCase();
}

type ReviewFilter = "all" | "needs_review" | "reviewed" | "no_records";

/** The filtered-to-nothing state: the filter is the reason, and it says so. */
function QuietNote({ filter, narrowed }: { filter: ReviewFilter; narrowed: boolean }) {
  if (narrowed) {
    // The date or the walker emptied it, not the review state. Saying "every
    // record has been decided" here would answer a question nobody asked.
    return (
      <div style={{ marginTop: 20, fontSize: 14.95, color: C.sub }} data-testid="walks-filter-empty">
        No walks match these filters. Widen the date range, or choose Anyone.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 20, fontSize: 14.95, color: C.sub }} data-testid="walks-filter-empty">
      {filter === "needs_review"
        ? "Nothing needs review — every record these walks produced has been decided."
        : filter === "no_records"
          ? "Every walk here produced records."
          : "No reviewed walks yet — decisions land here as records clear review."}
    </div>
  );
}

export function AllWalks(props: ConsoleViewProps) {
  const {
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    hrefForWalk,
    onOpenWalk,
    hrefForProject,
    onOpenProject,
  } = props;

  const dataSearch = scopedSearch(search, props.scopeSiteId);
  const { data, failure, refreshFailure } = useResource<WalksResponse>(
    `walks${resourceQuery(dataSearch, ["workspace", "site", "project", "limit"])}`,
    () => fetchWalks(dataSearch),
    refreshKey,
  );

  /** The chip row's review-state filter (design 1a) — a view of the one list,
   *  never a second query. */
  const [filter, setFilter] = useState<ReviewFilter>("all");
  /**
   * The design's chip row: when, and whose.
   *
   * Local rather than URL parameters, matching the review chips beside them
   * that have always been local — these three are one control group, and
   * splitting them across two mechanisms so that two of the three survived a
   * link would be worse than either choice made consistently.
   */
  const [range, setRange] = useState<WalkRange>("all");
  const [walker, setWalker] = useState<string | null>(null);
  const [order, setOrder] = useState<WalkOrder>("newest");
  /** One clock for every range test in a render — see `inWalkRange`. */
  const rangeNow = useMemo(() => new Date(), [data]);

  // Same rule as the item list: the data is whole, the render is paged, and
  // the budget is WALKS rather than days — a day holds one walk or nine.
  //
  // Called HERE, above the loading and failure returns, because that is what
  // a hook requires: one further down runs on the loaded render and not on
  // the loading one, and React counts hooks, not intentions.
  const byReview = data
    ? filter === "needs_review"
      ? data.walks.filter(needsReviewing)
      : filter === "reviewed" || filter === "no_records"
        ? data.walks.filter((walk) => reviewStateOf(walk) === filter)
        : data.walks
    : [];
  const shownWalks = byReview.filter(
    (walk) =>
      inWalkRange(walk, range, rangeNow) &&
      (walker === null || walkerLabel(walk) === walker),
  );
  const paged = usePagedList(`walks|${filter}|${range}|${walker ?? ""}|${order}`, shownWalks.length);

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) return <WalksSkeleton />;

  const siteNameOf = (jobSiteId: string) =>
    data.sites.find((site: ConsoleSite) => site.id === jobSiteId)?.name || undefined;
  const capped = data.walks.length < data.total;

  const needsReview = data.walks.filter(needsReviewing);
  const reviewed = data.walks.filter((walk) => reviewStateOf(walk) === "reviewed");
  // Walks that produced nothing: their own chip, so the chips add up to All.
  const noRecords = data.walks.filter((walk) => reviewStateOf(walk) === "no_records");
  const shown = shownWalks;
  /* The order turns the DAY STACK around; within a day, capture order
     stands. See `walkFilters` for why the sort is over days rather than over
     walks. */
  const days = groupByDay(shown);
  const ordered = order === "oldest" ? [...days].reverse() : days;
  const visibleDays = pagedDays(ordered, paged.shown);

  return (
    <ViewBody
      // Walks is a list page, so it shares Observations and Punch's data-table
      // measure instead of the narrower reading-column default.
      wide="table"
      // The header scrolls WITH the list (user-directed, 2026-09-04).
      // The crumb, the title, the stat band and the filter chips are
      // context for the rows underneath them, not a toolbar to keep in
      // reach — pinned, they took the top of the pane away from the
      // thing the reader came to read.
      header={
        <ViewHeader
          wide="table"
          crumb={
            data.activeWorkspace.type === "personal"
              ? "Personal"
              : data.activeWorkspace.name || "Team"
          }
          /*
            ---------- one statement of the numbers, not three ----------

            The header used to say the same thing three times over: this
            count, then a three-cell stat band (`1 WALKS / 1 TO REVIEW /
            9 RECORDS`), then the filter chips — which carry their own counts
            and are the things you can actually act on. Stacked above a list
            that was sometimes one row long.

            The band is the part that went. It was the only element of its
            kind on any list page in the console — Observations, Punch, Daily
            log and Photos all state their totals in one line and let the
            filters below carry the rest — and two of its three numbers were
            already on the chips. `records` is the one fact it had to itself,
            so it joins the count here rather than being dropped.
          */
          count={[
            capped
              ? `showing ${data.walks.length} of ${plural(data.total, "walk", "walks")}`
              : plural(data.total, "walk", "walks"),
            plural(
              data.walks.reduce((sum, walk) => sum + walk.itemCount, 0),
              "record",
              "records",
            ),
          ].join(" · ")}
          title="Walks"
        />
      }
    >
        {refreshFailure && <StaleBanner failure={refreshFailure} since={data.generatedAt} />}

        {data.walks.length === 0 ? (
          <EmptyPanel
            title={`No walks in ${
              data.activeWorkspace.type === "personal" ? "Personal" : "this team"
            } yet`}
            body={
              data.availableWorkspaces.length > 1
                ? "Nothing has been walked in this workspace. Try another workspace from the switcher."
                : "This fills in the moment somebody finishes a walk on site."
            }
          />
        ) : (
          <>
            {/* The design's chip row: when, whose, and which way up. It sits
                above the review chips rather than beside them because these
                three narrow the POPULATION while those partition what is
                left — and a row of six pills where three mean one thing and
                three another is a row nobody reads as two groups. */}
            <div
              data-testid="walks-controls"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                marginTop: 14,
              }}
            >
              <MenuControl
                testId="walks-range"
                label="When"
                value={WALK_RANGE_LABEL[range]}
                options={(["all", "today", "week", "month"] as WalkRange[]).map((key) => ({
                  value: key,
                  label: WALK_RANGE_LABEL[key],
                  meta: String(
                    byReview.filter((walk) => inWalkRange(walk, key, rangeNow)).length,
                  ),
                }))}
                onSelect={(next) => setRange(next)}
              />
              <MenuControl
                testId="walks-walker"
                label="Walker"
                value={walker ?? "Anyone"}
                options={[
                  { value: "", label: "Anyone", meta: String(byReview.length) },
                  ...walkerOptions(byReview, walkerLabel).map((entry) => ({
                    value: entry.value,
                    label: entry.value,
                    meta: String(entry.count),
                  })),
                ]}
                onSelect={(next) => setWalker(next === "" ? null : next)}
              />
              <span style={{ flex: 1 }} />
              <MenuControl
                testId="walks-order"
                label="Sort"
                value={WALK_ORDER_LABEL[order]}
                align="right"
                options={(["newest", "oldest"] as WalkOrder[]).map((key) => ({
                  value: key,
                  label: WALK_ORDER_LABEL[key],
                }))}
                onSelect={(next) => setOrder(next)}
                icon={
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M4 7h16M7 12h10M10 17h4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                }
              />
            </div>

            {/* Review-state chips (design 1a) — the caption row this replaces
                restated the list; these FILTER it. Counts are over everything
                fetched, so a chip's number never changes as chips are
                clicked.

                `FilterChip` is the Daily log's control, now shared: this page
                had built its own copy, 2px shorter and half a point smaller
                with a different edge, and nobody had chosen any of those
                differences. */}
            <div
              data-testid="walks-chips"
              style={{ display: "flex", gap: 6, marginTop: 14, marginBottom: 4 }}
            >
              {(
                [
                  { key: "all" as const, label: "All", count: data.walks.length },
                  { key: "needs_review" as const, label: "Needs review", count: needsReview.length },
                  { key: "reviewed" as const, label: "Reviewed", count: reviewed.length },
                  ...(noRecords.length > 0 || filter === "no_records"
                    ? [{ key: "no_records" as const, label: "No records", count: noRecords.length }]
                    : []),
                ]
              ).map((chip) => (
                <FilterChip
                  key={chip.key}
                  testId={`walks-chip-${chip.key}`}
                  label={chip.label}
                  count={chip.count}
                  active={filter === chip.key}
                  onClick={() => setFilter(chip.key)}
                />
              ))}
            </div>

            {days.length === 0 && (
              <QuietNote filter={filter} narrowed={range !== "all" || walker !== null} />
            )}

            {visibleDays.map((day) => (
              <div key={day.label} data-testid="walks-day" data-day={day.label}>
                {/* The day is an EYEBROW in the redesign, not a heading: the
                    rows under it are the content, and a 15/800 date competed
                    with the walk titles it was introducing. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    margin: "22px 0 8px 4px",
                  }}
                >
                  <span style={{ ...META, fontSize: 10.93, color: C.sub2 }}>{day.label}</span>
                  <span style={{ ...META, fontSize: 10.35, color: C.sub2 }} data-testid="walks-day-meta">
                    {dayMeta(day.walks)}
                  </span>
                </div>
                <Card
                  testId="walks-card"
                  style={{
                    padding: 0,
                    borderRadius: 14,
                    border: `1px solid ${C.hair}`,
                    boxShadow: `0 1px 2px ${C.softShadow}`,
                  }}
                >
                  {day.walks.map((walk, index) => (
                    <WalkRow
                      key={walk.id}
                      walk={walk}
                      siteName={siteNameOf(walk.jobSiteId)}
                      href={hrefForWalk(walk.id)}
                      onOpen={() => onOpenWalk(walk.id)}
                      projectHref={hrefForProject(walk.jobSiteId)}
                      onOpenProject={() => onOpenProject(walk.jobSiteId)}
                      first={index === 0}
                    />
                  ))}
                </Card>
              </div>
            ))}
            <PagedListEnd
              shown={paged.shown}
              total={shown.length}
              noun="walk"
              nouns="walks"
              sentinel={paged.sentinel}
            />
          </>
        )}
    </ViewBody>
  );
}

/** The first `budget` WALKS, with the days they fall on. */
function pagedDays(
  days: Array<{ label: string; walks: ConsoleWalkResponse[] }>,
  budget: number,
): Array<{ label: string; walks: ConsoleWalkResponse[] }> {
  const out: Array<{ label: string; walks: ConsoleWalkResponse[] }> = [];
  let left = budget;
  for (const day of days) {
    if (left <= 0) break;
    out.push(day.walks.length <= left ? day : { ...day, walks: day.walks.slice(0, left) });
    left -= day.walks.length;
  }
  return out;
}
